import React, { Suspense } from 'react';
import {
    Switch,
    Route,
} from 'react-router-dom';
import { FullPageLoader } from '../components/loaders/FullPageLoader';

import Restaurant from '../pages/restaurant/Restaurant';
import { pages } from '../store/actions/common.actions';
import {
    CART_ROUTE,
    CHECKOUT_CANCEL_ROUTE,
    CHECKOUT_CARD_ROUTE,
    CHECKOUT_GIFT_ROUTE,
    CHECKOUT_ROUTE,
    CHECKOUT_SUCCESS_ROUTE,
    CHECKOUT_SUMMARY_ROUTE,
    CHECKOUT_SUMMARY_UPDATED_ROUTE,
    CUSTOMER_ORDERS_ROUTE,
    CUSTOMER_ROUTE,
    FORGOT_PASSWORD_ROUTE,
    HOME_ROUTE,
    LOGIN_ROUTE,
    MAILING_SUBSCRIBE_ROUTE,
    MAILING_UNSUBSCRIBE_ROUTE,
    MAILING_UNSUBSCRIBED_ROUTE,
    REGISTRATION_ROUTE,
    REGISTRATION_CONFIRM_ROUTE,
    RESET_PASSWORD_ROUTE,
    SCHEDULE_ROUTE,
    POLICY_REFUND_ROUTE,
    SHOW_ORDER_ROUTE
} from './constants';
import { ProtectedRoute } from './ProtectedRoute';
import { useRestaurant } from '../contexts/restaurant.context';

const RestaurantSchedule = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/restaurant/RestaurantSchedule'));

const RefundPolicy = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/policy/RefundPolicy'));

const Registration = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/auth/Registration'));
const ConfirmRegistration = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/auth/ConfirmRegistration'));
const Login = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/auth/Login'));
const ForgotPassword = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/auth/ResetPassword'));

const Profile = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/customer/Profile'));
const Orders = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/customer/Orders'));

const UnsubscribedMailing = React.lazy(() => import('../pages/mailing/UnsubscribedMailing'));
const UnsubscribeMailing = React.lazy(() => import('../pages/mailing/UnsubscribeMailing'));
const SubscribeMailing = React.lazy(() => import('../pages/mailing/SubscribeMailing'));

const CheckoutSuccess = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/checkout/CheckoutSuccess'));
const CheckoutCancel = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/checkout/CheckoutCancel'));
const CheckoutSummaryUpdated = React.lazy(() => import('../pages/checkout/CheckoutSummaryUpdated'));
const CheckoutSummary = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/checkout/CheckoutSummary'));
const CheckoutCard = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/checkout/CheckoutCard'));
const CheckoutGift = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/checkout/CheckoutGift'));
const Checkout = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/checkout/Checkout'));

const Cart = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/cart/Cart'));

const ShowOrder = React.lazy(() => import(/* webpackPrefetch: true */ '../pages/show/ShowOrder'));

const Routes = () => {
    const { is_menu_require_auth, is_checkout_req_auth } = useRestaurant();
    
    const checkoutRequireAuth = is_menu_require_auth || is_checkout_req_auth;
    
    return (
        <Suspense fallback={<FullPageLoader />}>
            <Switch>
                <Route path={POLICY_REFUND_ROUTE} exact>
                    <RefundPolicy />
                </Route>
                <Route path={REGISTRATION_ROUTE} exact>
                    <Registration />
                </Route>
                <Route path={REGISTRATION_CONFIRM_ROUTE} exact>
                    <ConfirmRegistration />
                </Route>
                <Route path={LOGIN_ROUTE} exact>
                    <Login />
                </Route>
                <Route path={FORGOT_PASSWORD_ROUTE} exact>
                    <ForgotPassword />
                </Route>
                <Route path={RESET_PASSWORD_ROUTE} exact>
                    <ResetPassword />
                </Route>
                
                <ProtectedRoute path={CUSTOMER_ROUTE} exact isRequireAuth>
                    <Profile />
                </ProtectedRoute>
                <ProtectedRoute path={CUSTOMER_ORDERS_ROUTE} exact isRequireAuth>
                    <Orders />
                </ProtectedRoute>
                
                <Route path={MAILING_UNSUBSCRIBED_ROUTE} exact>
                    <UnsubscribedMailing page={pages.unsubscribed} />
                </Route>
                <Route path={MAILING_UNSUBSCRIBE_ROUTE} exact>
                    <UnsubscribeMailing page={pages.unsubscribe} />
                </Route>
                <Route path={MAILING_SUBSCRIBE_ROUTE} exact>
                    <SubscribeMailing page={pages.subscribe} />
                </Route>
                
                <Route path={SHOW_ORDER_ROUTE} exact>
                    <ShowOrder />
                </Route>
                
                <ProtectedRoute path={CHECKOUT_SUCCESS_ROUTE} exact isRequireAuth={is_menu_require_auth}>
                    <CheckoutSuccess page={pages.checkout} />
                </ProtectedRoute>
                <ProtectedRoute path={CHECKOUT_CANCEL_ROUTE} exact isRequireAuth={is_menu_require_auth}>
                    <CheckoutCancel page={pages.checkout} />
                </ProtectedRoute>
                <ProtectedRoute path={CHECKOUT_SUMMARY_UPDATED_ROUTE} exact isRequireAuth={checkoutRequireAuth}>
                    <CheckoutSummaryUpdated page={pages.checkoutSummaryUpdated} />
                </ProtectedRoute>
                <ProtectedRoute path={CHECKOUT_SUMMARY_ROUTE} exact isRequireAuth={checkoutRequireAuth}>
                    <CheckoutSummary page={pages.checkoutSummary} />
                </ProtectedRoute>
                <ProtectedRoute path={CHECKOUT_CARD_ROUTE} exact isRequireAuth={checkoutRequireAuth}>
                    <CheckoutCard page={pages.checkout} />
                </ProtectedRoute>
                <ProtectedRoute path={CHECKOUT_GIFT_ROUTE} exact isRequireAuth={checkoutRequireAuth}>
                    <CheckoutGift page={pages.gift} />
                </ProtectedRoute>
                <ProtectedRoute path={CHECKOUT_ROUTE} exact isRequireAuth={checkoutRequireAuth}>
                    <Checkout page={pages.checkout} />
                </ProtectedRoute>
                <ProtectedRoute path={CART_ROUTE} exact isRequireAuth={is_menu_require_auth}>
                    <Cart page={pages.cart} />
                </ProtectedRoute>
                <ProtectedRoute path={SCHEDULE_ROUTE} excat isRequireAuth={is_menu_require_auth}>
                    <RestaurantSchedule />
                </ProtectedRoute>
                <ProtectedRoute path={HOME_ROUTE} excat isRequireAuth={is_menu_require_auth}>
                    <Restaurant page={pages.restaurant} />
                </ProtectedRoute>
                
            </Switch>
        </Suspense>
    );
};

export { Routes };
